import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ChapterDialog({ open, onOpenChange, programId, chapter, defaultOrder, onSaved }) {
    const [form, setForm] = useState({ title: "", description: "", order: 0, hours: 1 });
    const [saving, setSaving] = useState(false);
    const isEdit = !!chapter;

    useEffect(() => {
        if (open) {
            setForm(
                chapter
                    ? {
                          title: chapter.title || "",
                          description: chapter.description || "",
                          order: chapter.order ?? 0,
                          hours: Number(chapter.hours ?? 1),
                      }
                    : { title: "", description: "", order: defaultOrder ?? 0, hours: 1 },
            );
        }
    }, [open, chapter, defaultOrder]);

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/chapters/${chapter.id}`, form);
                toast.success("Chapter updated");
            } else {
                await api.post(`/programs/${programId}/chapters`, form);
                toast.success("Chapter added");
            }
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="chapter-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        {isEdit ? "Edit chapter" : "New chapter"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Chapter title</Label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="e.g. Getting Started"
                            data-testid="chapter-title-input"
                        />
                    </div>
                    <div>
                        <Label>Description (optional)</Label>
                        <Input
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="One-line summary"
                            data-testid="chapter-desc-input"
                        />
                    </div>
                    <div>
                        <Label>Order</Label>
                        <Input
                            type="number"
                            value={form.order}
                            onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                            data-testid="chapter-order-input"
                        />
                    </div>
                    <div>
                        <Label>Estimated hours</Label>
                        <Input
                            type="number"
                            min="0"
                            step="0.25"
                            value={form.hours}
                            onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}
                            data-testid="chapter-hours-input"
                            placeholder="e.g. 1.5"
                        />
                        <div className="text-xs text-muted-foreground mt-1">
                            Adds to the program total hours shown to students.
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="chapter-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="chapter-save-btn"
                    >
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Add chapter"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
